import React, { useState, useEffect, useMemo } from 'react';
import { Select, Box, Text, Alert, Loader, Group, ActionIcon } from '@mantine/core';
import { IconSearch, IconAlertCircle } from '@tabler/icons-react';
import { entityService } from '../../services/entityServiceSingleton';
import { FunctionCatalogBrowserDialog } from '../dialogs';
import { debugLog } from '../../utils/debugLog';

interface FunctionCatalogSelectorProps {
    value: string | null;
    onChange: (value: string | null) => void;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    allowClear?: boolean;
}

export const FunctionCatalogSelector: React.FC<FunctionCatalogSelectorProps> = ({
    value,
    onChange,
    label = "Function Catalog",
    required = false,
    disabled = false,
    error = false,
    helperText,
    allowClear = true
}) => {
    const [functionCatalog, setFunctionCatalog] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [errorState, setErrorState] = useState<string | null>(null);
    const [browserOpen, setBrowserOpen] = useState(false);

    useEffect(() => {
        const loadFunctionCatalog = async () => {
            setLoading(true);
            setErrorState(null);

            try {
                const catalogItems = await entityService.getFunctionCatalog();
                debugLog(`🔍 FunctionCatalogSelector: Loaded ${catalogItems.length} function catalog items`);
                setFunctionCatalog(catalogItems);
            } catch (err) {
                console.error('Error loading function catalog:', err);
                setErrorState('Failed to load function catalog');
            } finally {
                setLoading(false);
            }
        };

        loadFunctionCatalog();
    }, [entityService]);

    const handleBrowserConfirm = (selectedIds: string[]) => {
        const selected = selectedIds.length > 0 ? selectedIds[0] : null;
        debugLog('[FunctionCatalogSelector] popup selected value:', selected, selectedIds);
        if (selected) {
            onChange(selected);
        }
        setBrowserOpen(false);
    };

    const selectData = useMemo(() => {
        const options = functionCatalog.map((item) => ({
            value: item.Id,
            label: item.DisplayName || item.Id
        }));
        // Ensure current value is representable even before catalog loads
        if (value && !functionCatalog.find(item => item.Id === value)) {
            options.unshift({ value, label: value });
        }
        return options;
    }, [functionCatalog, value]);

    return (
        <Box>
            <Group gap="xs" align="flex-end">
                <Select
                    flex={1}
                    label={label}
                    placeholder="Select a function catalog item..."
                    data={selectData}
                    value={value}
                    onChange={(val) => {
                        const selected = val || null;
                        debugLog('[FunctionCatalogSelector] dropdown selected value:', selected);
                        onChange(selected);
                    }}
                    required={required}
                    disabled={loading || disabled}
                    error={error}
                    searchable
                    clearable={allowClear}
                    rightSection={loading ? <Loader size={16} /> : undefined}
                />
                <ActionIcon
                    variant="light"
                    onClick={() => setBrowserOpen(true)}
                    disabled={disabled}
                    mb={4}
                >
                    <IconSearch size={16} />
                </ActionIcon>
            </Group>

            {errorState && (
                <Alert color="red" icon={<IconAlertCircle size={16} />} mt="xs">
                    {errorState}
                </Alert>
            )}

            {helperText && (
                <Text size="xs" c="dimmed" display="block" mt={4}>
                    {helperText}
                </Text>
            )}

            <FunctionCatalogBrowserDialog
                open={browserOpen}
                onClose={() => setBrowserOpen(false)}
                onConfirm={handleBrowserConfirm}
                title="Select Function Catalog Item"
                callerName="FunctionCatalogSelector"
                singleSelection={true}
                preloadedItems={functionCatalog}
            />
        </Box>
    );
};
